import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Typography, Box, Stack } from "@mui/material";

import TableComponent from "../../../shared/table/table";
import ModalComponent from "../../../shared/modal/modal";
import ListForm from "./createMailing/createMailing";

import getApiData from "../../../../hooks/apiRequests/getRequest";
import postApiData from "../../../../hooks/apiRequests/postsRequest";
import deleteApiData from "../../../../hooks/apiRequests/deleteRequest";
import { normalizePageParam } from "../../../../hooks/pagination";

import { useToast } from "../../../shared/toastMessages/toastProvider";
import css from "../../../../styles/portal/main.module.css";
import { theme } from "../../../../configs/theme";

const ManageMailing = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [data, setData] = useState();
  const [progress, setProgress] = useState(false);
  const [editStatus, setEditStatus] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [open, setOpen] = useState(false);

  const [listName, setListName] = useState("");
  const [description, setDescription] = useState("");
  const [icon, setIcon] = useState("");
  const [error, setError] = useState("");

  const query = searchParams.get("query") || "";
  const page = normalizePageParam(searchParams.get("page"));

  const fetchMailingLists = useCallback(async () => {
    setProgress(true);
    try {
      const response = await getApiData(
        `/Emailling/List?Search_filter=${encodeURIComponent(query)}&page_number=${page}`
      );
      if (!response?.data) {
        throw new Error("Empty mailing list response");
      }
      setData(response.data);
    } catch (err) {
      console.error(err);
      showToast("Failed to load data", "error");
      navigate("/login");
    } finally {
      setProgress(false);
    }
  }, [query, page, navigate, showToast]);

  useEffect(() => {
    fetchMailingLists();
  }, [fetchMailingLists]);

  const handleAddMailingList = async () => {
    if (!listName.trim() || !description.trim()) {
      setError("Both fields are required.");
      return;
    }

    try {
      const response = await postApiData("/Create/MailingList", {
        list_name: listName.trim(),
        description: description.trim(),
        icon: icon || "",
      });

      if (response?.error || !response?.data) {
        showToast("Failed to create mailing list", "error");
        return;
      }

      showToast("Mailing List Created Successfully", "success");
      handleClose();
      await fetchMailingLists();
    } catch (err) {
      console.error(err);
      showToast("Failed to create mailing list", "error");
    }
  };

  const removeMailing = async () => {
    try {
      const response = await deleteApiData(
        `/Delete/MailingList?MailingId=${selectedUser}`
      );

      if (!response) {
        showToast("Unable to delete mailing list", "error");
        return;
      }

      showToast("Mailing list removed", "success");
      handleClose();
      await fetchMailingLists();
    } catch (error) {
      console.error(error);
      showToast("Unable to delete mailing list", "error");
    }
  };

  // 🔹 Modal helpers
  const handleClose = () => {
    setOpen(false);
    setEditStatus(false);
    setSelectedUser(null);
    setListName("");
    setDescription("");
    setIcon("");
    setError("");
  };

  const handleAddOpen = () => {
    setEditStatus("Add");
    setOpen(true);
  };

  const handleDeleteRowOpen = (row) => {
    setSelectedUser(row);
    setEditStatus("Delete");
    setOpen(true);
  };

  const handleNavigateToEdit = (id) => {
    localStorage.setItem(
      "lastMailingListUrl",
      window.location.pathname + window.location.search
    );
    navigate(`/Portal/Mailing/Edit/${id}`);
  };

  const newpage = async (nextPage, search) => {
    setSearchParams({
      query: search || "",
      page: normalizePageParam(nextPage),
    });
  };

  const getModalTitle = () => {
    switch (editStatus) {
      case "Add":
        return "Create Mailing List";
      case "Delete":
        return "Delete Mailing List";
      default:
        return "";
    }
  };

  const renderModalContent = () => {
    switch (editStatus) {
      case "Add":
        return (
          <ListForm
            listName={listName}
            setListName={setListName}
            description={description}
            setDescription={setDescription}
            icon={icon}
            setIcon={setIcon}
            error={error}
          />
        );
      case "Delete":
        return (
          <Typography>
            Are you sure you want to delete this mailing list?
          </Typography>
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ px: { xs: 1, md: 2 }, pb: 3 }}>
      <Stack spacing={0.5} sx={{ py: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: "black" }}>
          Manage Mailing List
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Create and manage mailing lists, then open a list to edit its contacts.
        </Typography>
      </Stack>

      <div className={css.container}>
        <TableComponent
          apiData={data}
          progress={progress}
          noResults={!progress && Array.isArray(data?.data) && data.data.length === 0}
          columnNames={{
            list_name: "List Name",
            icon: "Icon",
            description: "Description",
            emails_number: "Number of Emails",
            time_created: "Time Created",
          }}
          handleAdd={handleAddOpen}
          handleOpen={handleNavigateToEdit}
          handleSpecDelete={handleDeleteRowOpen}
          theme={theme}
          mailingTable={false}
          newpage={newpage}
          query={query}
          pageNumber={page}
        />
      </div>

      <ModalComponent
        title={getModalTitle()}
        subtitle={
          editStatus === "Delete"
            ? "This will permanently remove the mailing list."
            : "Create a new list to organise contacts for campaigns."
        }
        open={open}
        handleOpen={handleClose}
        theme={theme}
        handleSubmit={
          editStatus === "Delete"
            ? removeMailing
            : handleAddMailingList
        }
        alternateSaveMessage={
          editStatus === "Delete" ? "Delete list" : "Create list"
        }
        submitColor={editStatus === "Delete" ? "error" : "primary"}
      >
        {renderModalContent()}
      </ModalComponent>
    </Box>
  );
};

export default ManageMailing;
